import Immutable from 'immutable';
import _difference from 'lodash/difference';
import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router';

import Context from '@bibliocommons/context';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import { stripTags } from '@bibliocommons/utils-html';

import ProgressBar from 'app/components/shared/ProgressBar';
import {
  trackCatalogExplore,
  trackContentClick,
  trackContentExplore,
  trackContentEngageLike,
  trackHoldingsConsumeAccess,
  trackHoldingsExplore
} from 'app/actions/AnalyticsGa4Actions';
import { fetchCatalogBibs } from 'app/actions/CatalogBibsActions';
import { fetchCheckouts } from 'app/actions/CheckoutActions';
import { fetchHolds } from 'app/actions/HoldActions';
import { addLike, removeLike } from 'app/actions/LikesActions';
import { getBibItemAvailabilites, getListItems } from 'app/actions/ListActions';
import { closeModal, openModal } from 'app/actions/ModalActions';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import { ShelfActionsButtonContainer } from 'app/components/bibs/shared/ShelfActionsButton';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import FlagUgcFormContainer from 'app/components/bibs/shared/ugc/FlagUgcButton/FlagUgcForm';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';
import UserCard from 'app/components/shared/UserCard';
import { METADATA_CATEGORY_TYPE, UI_COMPONENT_TYPE, UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { ITEMS_PER_PAGE_LIMIT, LIST_ITEM_TYPE } from 'app/constants/list';
import { GLOBAL_MESSAGING_ID } from 'app/constants/MessagingConstants';
import { mapMetadataIdToLibraryId } from 'app/helpers/bib/bibHelpers';
import {
  adaptUserToUserCardUser,
  getSchemaOrgGraph,
  isValidListId,
  mapListToAnalyticsListEntity,
  mapRawLike
} from 'app/helpers/list';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import composeGlobalFailureMessaging from 'app/helpers/messaging/composeGlobalFailureMessaging';
import composeGlobalSuccessMessaging from 'app/helpers/messaging/composeGlobalSuccessMessaging';
import useSessionStorage from 'app/hooks/useSessionStorage';
import { selectCurrentAccount, selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AlternativeFormatsOverlay from './AlternativeFormatsOverlay';
import ListPage from './ListPage';

const STORAGE_KEY_FORMATS_OVERLAY_METADATA_ID = 'list.storageOverlayMetadataId';
const FLAG_UGC_MODAL_KEY = 'flag-ugc-modal';

const ListPageContainer = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { pathname } = useLocation();
  const { userId: urlUserId } = useParams();
  const baseUrl = useSelector(state => state.getIn(['app', 'baseURL']));
  const currentAccount = useSelector(selectCurrentAccount);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser) ?? null;
  const { mobileApp: isMobileApp } = useContext(Context);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const localBranch = useSelector(state => state.getIn(['app', 'localBranch']) ?? null);
  const subdomain = useSelector(state => state.getIn(['app', 'libraryDomain']));
  const listState = useSelector(state => state.get('list') ?? Immutable.Map());
  const [formatsOverlayMetadataId, setFormatsOverlayMetadataId] = useSessionStorage(
    STORAGE_KEY_FORMATS_OVERLAY_METADATA_ID
  );

  const availabilityByMetadataId = listState.get('availabilityByMetadataId');
  const bibsByMetadataId = listState.get('bibsByMetadataId');
  const catalogBibsByMetadataId = listState.get('catalogBibsByMetadataId');
  const items = listState.get('items');
  const isFetchingItems = listState.get('isFetchingItems');
  const list = listState.get('list')?.toJS() ?? null;
  const manifestationsByMetadataId = listState.get('manifestationsByMetadataId') ?? Immutable.Map();
  const parsedLike = listState.get('like')?.toJS() ?? null;
  const parsedNumLikes = listState.get('numLikes') ?? Number.NaN;
  const pagination = listState.get('pagination');
  const creatorUser = listState.get('user')?.toJS() ?? null;

  const { listId, userId: creatorUserId } = list;
  const [like, setLike] = useState(parsedLike);
  const [numLikes, setNumLikes] = useState(parsedNumLikes);

  const listTitle = list?.title;
  const relatedMetadataId = list?.relatedMetadataId ?? null;
  const relatedBib = bibsByMetadataId.get(relatedMetadataId) ?? null;
  const pageTitle = relatedBib?.title ?? listTitle;
  const preferredLocations = currentAccount?.get('preferredLocations');

  useEffect(() => {
    const currentLibraryId = String(currentLibrary.get('id'));
    const allCurrentLibraryMetadataIds = bibsByMetadataId
      .keySeq()
      .toArray()
      .filter(metadataId => {
        return mapMetadataIdToLibraryId(metadataId) === currentLibraryId;
      });

    const fetchedAvailabilityMetadataIds = availabilityByMetadataId.keySeq().toArray();
    const metadataIdsToFetch = _difference(allCurrentLibraryMetadataIds, fetchedAvailabilityMetadataIds);
    if (!metadataIdsToFetch.length) {
      return;
    }

    dispatch(
      getBibItemAvailabilites({
        localBranch,
        metadataIds: metadataIdsToFetch,
        preferredLocations: preferredLocations?.toJS() ?? {},
        subdomain
      })
    );
  }, [
    availabilityByMetadataId,
    bibsByMetadataId,
    currentLibrary,
    dispatch,
    localBranch,
    preferredLocations,
    subdomain
  ]);

  useEffect(() => {
    const metadataIds = manifestationsByMetadataId.get(formatsOverlayMetadataId)?.toJS() ?? [];
    if (!metadataIds.length) {
      return;
    }

    dispatch(fetchCatalogBibs(metadataIds));

    const accountId = currentAccount?.get('id');

    if (accountId) {
      dispatch(fetchCheckouts({ accountId }));
      dispatch(fetchHolds({ accountId }));
    }
  }, [currentAccount, dispatch, formatsOverlayMetadataId, manifestationsByMetadataId]);

  // set focus to first item of next "page" when more items are first loaded
  useEffect(() => {
    const { limit, page } = pagination?.toJS() ?? {};
    if (page === 1) {
      return;
    }

    const indexOfFirstItemOfNewPage = (page - 1) * limit;
    const itemToFocus = items.get(indexOfFirstItemOfNewPage);
    if (!itemToFocus) {
      return;
    }

    const { itemId } = itemToFocus;

    const itemEl = document.querySelector(`[data-key="${itemId}"]`);
    if (!itemEl) {
      return;
    }

    setFocusWithinElement(itemEl);
  }, [items, pagination]);

  if (!isValidListId(listId)) {
    // TODO: can we return a 4xx HTTP status code?
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  if (urlUserId !== creatorUserId) {
    // TODO: can we return a 4xx HTTP status code?
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const closeFlagUgcModal = () => {
    dispatch(closeModal(FLAG_UGC_MODAL_KEY));
  };

  const openFlagUgcModal = () => {
    dispatch(openModal(FLAG_UGC_MODAL_KEY));
  };

  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
  };

  const handleCopyCoversClick = () => {
    const container = document.createElement('div');
    const range = document.createRange();
    const selection = window.getSelection();

    container.contentEditable = 'true';
    container.id = 'img-container';
    container.style.height = '1px';
    container.style.width = '1px';

    document.body.querySelectorAll('img.cp-jacket-cover').forEach(image => {
      container.append(image.cloneNode());
    });
    document.body.append(container);

    range.selectNodeContents(container);
    selection.removeAllRanges();
    selection.addRange(range);

    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    container.contentEditable = 'false';
    container.remove();

    dispatch({
      type: 'copy-covers-success',
      messaging: composeGlobalSuccessMessaging('copy-covers-success', {
        defaultMessage: 'Cover images have been copied to the clipboard',
        id: 'list.notification_copy_covers_success'
      })
    });
  };

  const handleFlagClick = () => {
    openFlagUgcModal();
  };

  const handleInfoClick = () => {
    setIsInfoModalOpen(true);
  };

  const trackLikeClick = () => {
    dispatch(
      trackContentEngageLike({
        list: mapListToAnalyticsListEntity(list),
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.BUTTON,
          ui_container_type: UI_CONTAINER_TYPE.LIST_HEADER
        }
      })
    );
  };

  const handleLikeClick = () => {
    const contentId = list?.listId;
    if (!contentId) {
      throw new Error('No listId found');
    }

    const isLiked = Boolean(like);

    if (isLiked) {
      dispatch(
        removeLike({
          id: like.likeId,
          contentId,
          postSuccess: () => {
            setLike(null);
            setNumLikes(currentNumLikes => Math.max(currentNumLikes - 1, 0));
          }
        })
      );
    } else {
      trackLikeClick();
      dispatch(
        addLike({
          apiFailure: apiError => {
            // map generic "AlreadyExists" into list-specific message
            if (apiError?.error?.classification === 'AlreadyExists') {
              return {
                messaging: {
                  [GLOBAL_MESSAGING_ID]: {
                    message: {
                      defaultMessage: 'You have already liked this List',
                      id: 'errors.alread_liked_list',
                      type: 'Failure'
                    }
                  }
                }
              };
            }

            return apiError;
          },
          contentId,
          postSuccess: res => {
            setLike(mapRawLike(res));
            setNumLikes(currentNumLikes => currentNumLikes + 1);
          }
        })
      );
    }
  };

  const handleLoadMoreItemsClick = () => {
    const { page, pages: numTotalPages } = pagination?.toJS() ?? {};
    const nextPage = page + 1;

    if (nextPage > numTotalPages) {
      throw new Error('invalid page request');
    }

    dispatch(getListItems({ itemsPerPageLimit: ITEMS_PER_PAGE_LIMIT, listId, page: nextPage, subdomain }));
  };

  const handlePrintClick = () => {
    window?.print();
  };

  const notifyCopyToClipboardSuccess = () => {
    dispatch({
      type: 'copy-to-clipboard-succes',
      messaging: composeGlobalSuccessMessaging('copy-to-clipboard-success', {
        defaultMessage: 'List URL has been copied to the clipboard',
        id: 'list.notification_copy_to_clipboard_success'
      })
    });
  };

  const handleViewFormatsClick = ({ linkText, metadataId, position }) => {
    if (!metadataId) {
      throw new Error('missing metadataId');
    }

    const bib = bibsByMetadataId.get(metadataId)?.toJS() ?? null;
    if (!bib) {
      throw new Error(`No bib found with metadataId: ${metadataId}`);
    }

    const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    dispatch(
      trackHoldingsExplore({
        bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_page: page,
          ui_content_position: position,
          ui_content_total_count: numTotalItems
        }
      })
    );

    setFormatsOverlayMetadataId(metadataId);
  };

  const notifyCopyToClipboardFailure = () => {
    dispatch({
      type: 'copy-to-clipboard-failure',
      messaging: composeGlobalFailureMessaging('copy-to-clipboard-failure', {
        defaultMessage: 'Could not copy to clipboard',
        id: 'list.notification_copy_to_clipboard_failure'
      })
    });
  };

  const handleShareClick = () => {
    const pageUrl = document.location.href;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(pageUrl)
        .then(notifyCopyToClipboardSuccess)
        .catch(notifyCopyToClipboardFailure);
    } else {
      const textArea = document.createElement('textarea');

      textArea.value = pageUrl;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const isCopied = document.execCommand('copy');

        if (isCopied) {
          notifyCopyToClipboardSuccess();
        } else {
          notifyCopyToClipboardFailure();
        }
      } catch {
        notifyCopyToClipboardFailure();
      }

      document.body.removeChild(textArea);
    }
  };

  const trackUserCardClick = ({ linkText }) => {
    dispatch(
      trackContentExplore({
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT
        }
      })
    );
  };

  const renderUserCard = () => {
    if (!creatorUser) {
      return null;
    }
    return (
      <UserCard
        hasAvatar
        hasBadge
        hasLibraryFullName
        ugcType="list"
        user={adaptUserToUserCardUser(creatorUser)}
        trackProfileLinkClick={trackUserCardClick}
      />
    );
  };

  const renderAddToShelfButton = metadataId => {
    const listBib = bibsByMetadataId.get(metadataId)?.toJS();
    const { title: listBibTitle = '' } = listBib ?? {};

    const screenReaderMessage = intl.formatMessage(
      {
        defaultMessage: `Add ${listBibTitle} to your for later shelf`,
        id: 'ada_add_to_named_shelf'
      },
      {
        shelf: 'for_later',
        title: listBibTitle
      }
    );

    return (
      <ShelfActionsButtonContainer
        iconOnly
        metadataId={metadataId}
        screenReaderMessageAddToShelf={screenReaderMessage}
      />
    );
  };

  const isStaffList = creatorUser?.isStaff ?? false;

  const { displayName, homeLibraryFullName, homeLibrarySubdomain } = creatorUser ?? {};
  const { description, imageUrl, title } = list ?? {};

  const safeDescription = stripTags(description ?? '');
  const metaTitle = intl.formatMessage(
    {
      defaultMessage:
        '{listTitle} — a {isStaffList, select, true {staff-created} other {community-created}} list from {source}',
      id: 'list.meta_page_title'
    },
    {
      isStaffList,
      listTitle: title,
      source: isStaffList ? homeLibraryFullName : displayName
    }
  );

  const creatorLibraryHost = baseUrl.replace(currentLibrary?.get('domain') ?? '', homeLibrarySubdomain);
  const canonicalListUrl = `${creatorLibraryHost}${pathname}`;

  const linkedData = Immutable.Map(
    getSchemaOrgGraph({
      bibsByMetadataId: bibsByMetadataId?.toJS() ?? {},
      canonicalListUrl,
      creatorLibraryHost,
      items: items.slice(0, ITEMS_PER_PAGE_LIMIT)?.toJS() ?? [],
      list
    })
  );

  const trackBibAuthorClick = ({ bib, linkText, position }) => {
    const { metadataId } = bib;

    const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    dispatch(
      trackCatalogExplore({
        bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_CREATORS,
          metadata_value: linkText
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_page: page,
          ui_content_position: position,
          ui_content_total_count: numTotalItems
        }
      })
    );
  };

  const trackBibImageClick = ({ item, position }) => {
    const { metadataId } = item;

    const bib = bibsByMetadataId.get(metadataId)?.toJS() ?? null;
    if (!bib) {
      throw new Error(`No bib found with metadataId: ${metadataId}`);
    }

    const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    dispatch(
      trackContentClick({
        bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
          metadata_value: bib.title
        },
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_page: page,
          ui_content_position: position,
          ui_content_total_count: numTotalItems
        }
      })
    );
  };

  const trackItemTitleClick = ({ item, linkText, position }) => {
    const { type, url } = item;
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    if (type === LIST_ITEM_TYPE.WEB) {
      dispatch(
        trackHoldingsConsumeAccess({
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_ELECTRONIC_RESOURCE_LIST,
            metadata_value: url
          },
          ui: {
            ui_component_label: linkText,
            ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: position,
            ui_content_total_count: numTotalItems
          }
        })
      );
    } else {
      const { metadataId } = item;

      const bib = bibsByMetadataId.get(metadataId)?.toJS() ?? null;
      if (!bib) {
        throw new Error(`No bib found with metadataId: ${metadataId}`);
      }

      const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;

      dispatch(
        trackContentClick({
          bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
            metadata_value: bib.title
          },
          ui: {
            ui_component_label: linkText,
            ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: position,
            ui_content_total_count: numTotalItems
          }
        })
      );
    }
  };

  const trackRelatedBibAuthorClick = ({ author, bib }) => {
    const { metadataId } = bib;

    const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;

    dispatch(
      trackCatalogExplore({
        bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_CREATORS,
          metadata_value: author
        },
        ui: {
          ui_component_label: author,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_type: UI_CONTAINER_TYPE.LIST_HEADER
        }
      })
    );
  };

  const trackRelatedBibTitleClick = ({ linkText }) => {
    const bib = relatedBib?.toJS();
    if (!bib) {
      return;
    }

    const { metadataId, title: bibTitle } = bib;
    const availability = availabilityByMetadataId.get(metadataId)?.toJS() ?? null;
    const { count: numTotalItems } = pagination?.toJS() ?? {};

    dispatch(
      trackContentClick({
        bib: mapDisplayBibToAnalyticsBibEntity(bib, availability),
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
          metadata_value: bibTitle
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_total_count: numTotalItems
        }
      })
    );
  };

  const trackWebUrlClick = ({ item, linkText, position }) => {
    const { url } = item;

    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    dispatch(
      trackHoldingsConsumeAccess({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_ELECTRONIC_RESOURCE_LIST,
          metadata_value: url
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_page: page,
          ui_content_position: position,
          ui_content_total_count: numTotalItems
        }
      })
    );
  };

  const globalCss = `@page {
    margin: 0.75in 0.5in;
  }`;

  return (
    <>
      <style>{globalCss}</style>
      <DocumentHeadContainer
        description={safeDescription}
        excludeLibraryFromMetaTitle
        imageUrl={imageUrl}
        linkedData={linkedData}
        pageTitle={metaTitle}
        pageUrl={canonicalListUrl}
      />
      <ProgressBar active={isFetchingItems} />
      <BackToLinkManager pageId="list" />
      <ModalContainer modalKey={FLAG_UGC_MODAL_KEY} handleClose={closeFlagUgcModal}>
        <FlagUgcFormContainer id={Number(list.listId)} handleClose={closeFlagUgcModal} />
      </ModalContainer>
      <AlternativeFormatsOverlay
        alerternativeFormatMetadataIds={manifestationsByMetadataId.get(formatsOverlayMetadataId)?.toJS() ?? []}
        catalogBib={catalogBibsByMetadataId.get(formatsOverlayMetadataId)}
        closeOverlay={() => {
          setFormatsOverlayMetadataId(null);
        }}
        isOpen={Boolean(formatsOverlayMetadataId)}
      />
      <ListPage
        availabilityByMetadataId={availabilityByMetadataId?.toJS() ?? {}}
        bibsByMetadataId={bibsByMetadataId?.toJS() ?? {}}
        closeInfoModal={closeInfoModal}
        currentLibrary={currentLibrary}
        currentUser={currentUser}
        handleCopyCoversClick={handleCopyCoversClick}
        handleFlagClick={handleFlagClick}
        handleInfoClick={handleInfoClick}
        handleLikeClick={handleLikeClick}
        handleLoadMoreItemsClick={handleLoadMoreItemsClick}
        handlePrintClick={handlePrintClick}
        handleShareClick={handleShareClick}
        handleViewFormatsClick={handleViewFormatsClick}
        intl={intl}
        isFetchingItems={isFetchingItems}
        isInfoModalOpen={isInfoModalOpen}
        isLikedByCurrentUser={Boolean(like)}
        isMobileApp={isMobileApp}
        isStaffList={isStaffList}
        items={items?.toJS() ?? []}
        list={list}
        manifestationsByMetadataId={manifestationsByMetadataId?.toJS()}
        numLikes={numLikes}
        pageTitle={pageTitle}
        pagination={pagination?.toJS() ?? {}}
        renderAddToShelfButton={renderAddToShelfButton}
        renderUserCard={renderUserCard}
        trackBibAuthorClick={trackBibAuthorClick}
        trackBibImageClick={trackBibImageClick}
        trackItemTitleClick={trackItemTitleClick}
        trackRelatedBibAuthorClick={trackRelatedBibAuthorClick}
        trackRelatedBibTitleClick={trackRelatedBibTitleClick}
        trackWebUrlClick={trackWebUrlClick}
      />
    </>
  );
};

export default ListPageContainer;
